import moment from 'moment-timezone';
import { APP_TIMEZONE } from './timezone';
import { getOperationalDayKeyFast } from './locationHours';

export const DASHBOARD_CACHE_TTL_MS = 15 * 60 * 1000;
export const DASHBOARD_MAX_CACHE_ENTRIES = 6;

const BROWNSVILLE_SOURCE_KEYS = [
  'Brownsville',
  'BrownsvilleEPL',
  'Brownsville_EPL',
  'EPL_Brownsville',
  'EPLBrownsville',
];

const EMPTY_SOURCE = { labels: [], tables: [], game_name: [] };

export const buildDashboardCacheKey = (startDate, endDate) => `${startDate}_${endDate}`;

export const extractSources = (raw) => {
  const mcallen = raw?.EPL || EMPTY_SOURCE;
  const brownsvilleKey = raw
    ? BROWNSVILLE_SOURCE_KEYS.find((key) => raw[key])
    : undefined;
  const brownsville = brownsvilleKey ? raw[brownsvilleKey] : EMPTY_SOURCE;
  return { mcallen, brownsville };
};

export const getDaysInRange = (startDate, endDate) => {
  const days = [];
  const start = moment(startDate).tz(APP_TIMEZONE).startOf('day');
  const end = moment(endDate).tz(APP_TIMEZONE).startOf('day');

  let current = start.clone();
  while (current.isSameOrBefore(end, 'day')) {
    days.push(current.clone());
    current.add(1, 'day');
  }

  return days;
};

export const getOperationalDateRange = (startDate, endDate) => {
  const start = moment(startDate).tz(APP_TIMEZONE).startOf('day');
  const end = moment(endDate).tz(APP_TIMEZONE).startOf('day');
  const isSingleDay = start.isSame(end, 'day');

  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.clone().add(1, 'day').format('YYYY-MM-DD'),
    selectedEndDate: end.format('YYYY-MM-DD'),
    isSingleDay,
  };
};

export { getOperationalDayKeyFast };

const emptySide = () => ({ labels: [], tables: [], game_name: [] });

const createDayBucket = (dayKey, displayDate) => ({
  date: dayKey,
  displayDate,
  analysisData: {
    mcallen: emptySide(),
    brownsville: emptySide(),
  },
  error: null,
});

const pushRecord = (bucket, timestamp, index, source) => {
  bucket.labels.push(timestamp);
  bucket.tables.push(source.tables[index]);
  bucket.game_name.push(source.game_name[index]);
};

/** Single-pass filter + optional day-wise split */
export const processDashboardAnalytics = ({
  rawData,
  startDate,
  endDate,
  isSingleDay,
  rangeStart,
  rangeEnd,
}) => {
  if (!rawData) {
    return { filteredData: null, dayWiseData: {}, isMultiDay: !isSingleDay };
  }

  const { mcallen, brownsville } = extractSources(rawData);

  const multiDay = !isSingleDay;
  const days = getDaysInRange(rangeStart || startDate, rangeEnd || endDate);
  const validKeys = new Set();
  const dayWiseData = {};

  days.forEach((day) => {
    const dayKey = day.format('YYYY-MM-DD');
    validKeys.add(dayKey);
    if (multiDay) {
      dayWiseData[dayKey] = createDayBucket(dayKey, day.format('MMM DD, YYYY'));
    }
  });

  const filteredData = {
    mcallen: emptySide(),
    brownsville: emptySide(),
  };

  const processSide = (sourceKey, source) => {
    const labels = source?.labels || [];

    for (let index = 0; index < labels.length; index += 1) {
      const timestamp = labels[index];
      const dayKey = getOperationalDayKeyFast(timestamp, sourceKey);
      if (!dayKey || !validKeys.has(dayKey)) continue;

      pushRecord(filteredData[sourceKey], timestamp, index, source);

      if (multiDay && dayWiseData[dayKey]) {
        pushRecord(dayWiseData[dayKey].analysisData[sourceKey], timestamp, index, source);
      }
    }
  };

  processSide('mcallen', mcallen);
  processSide('brownsville', brownsville || EMPTY_SOURCE);

  return {
    filteredData,
    dayWiseData: multiDay ? dayWiseData : {},
    isMultiDay: multiDay,
  };
};

export const trimDashboardCache = (cache, cacheKeys) => {
  const nextCache = { ...cache };
  const nextKeys = [...cacheKeys];

  while (nextKeys.length > DASHBOARD_MAX_CACHE_ENTRIES) {
    const oldestKey = nextKeys.shift();
    delete nextCache[oldestKey];
  }

  return { cache: nextCache, cacheKeys: nextKeys };
};

export const isDashboardCacheFresh = (entry) =>
  Boolean(entry?.fetchedAt && Date.now() - entry.fetchedAt < DASHBOARD_CACHE_TTL_MS);

export const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
